/**
 * BankFormModal - Universal Bank Information Modal Component
 * Handles creation and editing of bank information for merchants
 * 
 * Features:
 * - Support for WIREBANK and ACH account types
 * - Real-time validation
 * - Bank address autocomplete
 * - Can work with or without API (controlled/uncontrolled mode)
 * - Edit mode support
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import AddressAutocomplete from 'components/AddressAutocomplete';
import NotificationBanner from 'components/NotificationBanner';
import {
  validateBankName,
  validateAccountName,
  validateAccountNumber,
  validateRoutingNumber,
  validateBankAddress,
  filterAccountNumber,
  filterRoutingNumber
} from 'modules/InputsValidations';

const BankFormModal = ({ 
  isOpen = false,
  toggle = () => {},
  title = 'Add Bank Information',
  onSave = () => {},
  bankData = null, // For editing
  loading = false,
  // API mode props (optional)
  apiEndpoint = null,
  apiService = null,
}) => {
  
  // Form state
  const [formData, setFormData] = useState({
    type: '',
    bankName: '',
    accountName: '',
    number: '',
    routingNumber: '',
    bankAddress: '',
    bankCity: '',
    bankState: '',
    bankCountry: '',
    bankZipCode: ''
  });
  
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState({ show: false, type: 'success', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form with editing data or reset
  useEffect(() => {
    if (isOpen) {
      if (bankData) {
        // Edit mode
        setFormData({
          type: bankData.type || '',
          bankName: bankData.bankName || '',
          accountName: bankData.accountName || '',
          number: bankData.number || bankData.accountNumber || '',
          routingNumber: bankData.routingNumber || '',
          bankAddress: bankData.bankAddress || bankData.bankCity || '',
          bankCity: bankData.bankCity || '',
          bankState: bankData.bankState || '',
          bankCountry: bankData.bankCountry || '',
          bankZipCode: bankData.bankZipCode || '',
          id: bankData.id
        });
      } else {
        // Create mode
        setFormData({
          type: '',
          bankName: '',
          accountName: '',
          number: '',
          routingNumber: '',
          bankAddress: '',
          bankCity: '',
          bankState: '',
          bankCountry: '',
          bankZipCode: ''
        });
      }
      setErrors({});
      setNotification({ show: false, type: 'success', message: '' });
    }
  }, [isOpen, bankData]);

  // Notification helpers
  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => {
      setNotification({ show: false, type: 'success', message: '' });
    }, 5000);
  };

  const closeNotification = () => {
    setNotification({ show: false, type: 'success', message: '' });
  };

  // Handle input changes with validation
  const handleInputChange = (field, value) => {
    let processedValue = value;
    
    // Apply filters
    if (field === 'number') {
      processedValue = filterAccountNumber(value);
    } else if (field === 'routingNumber') {
      processedValue = filterRoutingNumber(value);
    } else if (field === 'bankName' || field === 'accountName') {
      processedValue = value.slice(0, 255);
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: processedValue
    }));

    // Real-time validation
    validateField(field, processedValue);
  };

  // Validate individual field
  const validateField = (field, value) => {
    let validationResult = { isValid: true, message: '' };
    
    switch (field) {
      case 'bankName':
        validationResult = validateBankName(value);
        break;
      case 'accountName':
        validationResult = validateAccountName(value);
        break;
      case 'number':
        validationResult = validateAccountNumber(value);
        break;
      case 'routingNumber':
        validationResult = validateRoutingNumber(value);
        break;
      case 'bankAddress':
        validationResult = validateBankAddress(value);
        break;
      default:
        break;
    }

    if (!validationResult.isValid) {
      setErrors(prev => ({
        ...prev,
        [field]: validationResult.message
      }));
    } else {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle address autocomplete change
  const handleAddressChange = (text) => {
    setFormData(prev => ({
      ...prev,
      bankAddress: text,
      bankCity: '',
      bankState: '',
      bankCountry: '',
      bankZipCode: ''
    }));
    
    // Clear location-related errors
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.bankCity;
      delete newErrors.bankState;
      delete newErrors.bankCountry;
      delete newErrors.bankAddress;
      return newErrors;
    });
  };

  // Handle address autocomplete selection
  const handleAddressSelect = (item) => {
    const label = item?.label || '';
    const details = item?.details || {};
    const city = details.city || '';
    const state = details.state || '';
    const country = details.country || '';
    const zipCode = details.zipCode || '';
    
    setFormData(prev => ({
      ...prev,
      bankAddress: city, // Show city in the main input
      bankCity: city,
      bankState: state,
      bankCountry: country,
      bankZipCode: zipCode
    }));

    // Clear errors for autocompleted fields
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.bankCity;
      delete newErrors.bankState;
      delete newErrors.bankCountry;
      delete newErrors.bankAddress;
      return newErrors;
    });
  };

  // Validate entire form
  const validateForm = () => {
    const newErrors = {};
    
    // Check if user has started filling (at least one field has data)
    const hasStartedFilling = formData.type || formData.bankName || formData.accountName || 
      formData.number || formData.routingNumber || formData.bankAddress;
    
    // If completely empty, it's valid (optional)
    if (!hasStartedFilling) {
      return newErrors;
    }
    
    // If started filling, all required fields must be filled
    if (!formData.type || formData.type.trim() === '') {
      newErrors.type = 'Account type is required';
    }
    
    const bankNameValidation = validateBankName(formData.bankName);
    if (!bankNameValidation.isValid) {
      newErrors.bankName = bankNameValidation.message;
    }
    
    const accountNameValidation = validateAccountName(formData.accountName);
    if (!accountNameValidation.isValid) {
      newErrors.accountName = accountNameValidation.message;
    }
    
    const accountNumberValidation = validateAccountNumber(formData.number);
    if (!accountNumberValidation.isValid) {
      newErrors.number = accountNumberValidation.message;
    }
    
    const routingNumberValidation = validateRoutingNumber(formData.routingNumber);
    if (!routingNumberValidation.isValid) {
      newErrors.routingNumber = routingNumberValidation.message;
    }
    
    const bankAddressValidation = validateBankAddress(formData.bankAddress);
    if (!bankAddressValidation.isValid) {
      newErrors.bankAddress = bankAddressValidation.message;
    }
    
    return newErrors;
  };

  // Check if form is complete
  const isFormComplete = () => {
    const requiredFields = ['type', 'bankName', 'accountName', 'number', 'routingNumber', 'bankAddress'];
    const allFilled = requiredFields.every(field => {
      const value = formData[field];
      return value && value.trim() !== '';
    });
    return allFilled && Object.keys(errors).length === 0;
  };

  // Handle save
  const handleSave = async () => {
    // Final validation
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      showNotification('error', 'Please fix the errors in the form');
      return;
    }

    // Check if user has started filling
    const hasStartedFilling = formData.type || formData.bankName || formData.accountName || 
      formData.number || formData.routingNumber || formData.bankAddress;
    
    if (!hasStartedFilling) {
      showNotification('error', 'Please fill in at least some bank information');
      return;
    }

    // If API mode is enabled
    if (apiEndpoint && apiService) {
      setIsSubmitting(true);
      try {
        const result = await apiService.post(apiEndpoint, formData);
        showNotification('success', 'Bank information saved successfully!');
        onSave(result, bankData);
        toggle();
      } catch (error) {
        console.error('Error saving bank:', error);
        showNotification('error', `Error saving bank: ${error.message}`);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Callback mode
      onSave(formData, bankData);
      toggle();
    }
  };

  const isEditMode = !!bankData;
  const isBusy = loading || isSubmitting;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        <i className="ni ni-money-coins mr-2"></i>
        {title}
      </ModalHeader>
      <ModalBody>
        {/* Notification Banner */}
        <NotificationBanner
          type={notification.type}
          message={notification.message}
          show={notification.show}
          onClose={closeNotification}
        />

        <Row>
          <Col md="6">
            <FormGroup>
              <Label for="type">Account Type *</Label>
              <Input
                id="type"
                type="select"
                value={formData.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
                className={errors.type ? 'is-invalid' : ''}
              >
                <option value="">Select account type</option>
                <option value="WIREBANK">WIREBANK</option>
                <option value="ACH">ACH</option>
              </Input>
              {errors.type && (
                <div className="invalid-feedback d-block">{errors.type}</div>
              )}
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label for="bankName">Bank Name *</Label>
              <Input
                id="bankName"
                type="text"
                value={formData.bankName}
                onChange={(e) => handleInputChange('bankName', e.target.value)}
                placeholder="Enter bank name"
                className={errors.bankName ? 'is-invalid' : ''}
                maxLength={255}
              />
              {errors.bankName && (
                <div className="invalid-feedback d-block">{errors.bankName}</div>
              )}
            </FormGroup>
          </Col>
        </Row>
        
        <Row>
          <Col md="6">
            <FormGroup>
              <Label for="accountName">Account Name *</Label>
              <Input
                id="accountName"
                type="text"
                value={formData.accountName}
                onChange={(e) => handleInputChange('accountName', e.target.value)}
                placeholder="Enter account name"
                className={errors.accountName ? 'is-invalid' : ''}
                maxLength={255}
              />
              {errors.accountName && (
                <div className="invalid-feedback d-block">{errors.accountName}</div>
              )}
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label for="accountNumber">Account Number *</Label>
              <Input
                id="accountNumber"
                type="text"
                value={formData.number}
                onChange={(e) => handleInputChange('number', e.target.value)}
                placeholder="Enter account number"
                className={errors.number ? 'is-invalid' : ''}
              />
              {errors.number && (
                <div className="invalid-feedback d-block">{errors.number}</div>
              )}
              <small className="text-muted">Digits only, max 19</small>
            </FormGroup>
          </Col>
        </Row>
        
        <Row>
          <Col md="6">
            <FormGroup>
              <Label for="routingNumber">Routing Number *</Label>
              <Input
                id="routingNumber"
                type="text"
                value={formData.routingNumber}
                onChange={(e) => handleInputChange('routingNumber', e.target.value)}
                placeholder="Enter routing number"
                className={errors.routingNumber ? 'is-invalid' : ''}
              />
              {errors.routingNumber && (
                <div className="invalid-feedback d-block">{errors.routingNumber}</div>
              )}
              <small className="text-muted">Exactly 9 digits</small>
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label for="bankAddress">Bank City *</Label>
              <AddressAutocomplete
                value={formData.bankAddress}
                onChange={handleAddressChange}
                onSelect={handleAddressSelect}
                placeholder="Start typing bank address..."
                className={errors.bankAddress ? 'is-invalid' : ''}
              />
              {errors.bankAddress && (
                <div className="invalid-feedback d-block">{errors.bankAddress}</div>
              )}
            </FormGroup>
          </Col>
        </Row>
      </ModalBody>
      
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={isBusy}>
          Cancel
        </Button>
        <Button 
          color="primary" 
          onClick={handleSave} 
          disabled={isBusy || !isFormComplete()}
        >
          {isBusy ? (
            <>
              <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
              {isEditMode ? 'Updating...' : 'Adding...'}
            </>
          ) : (
            <>
              <i className="ni ni-check-bold mr-2"></i>
              {isEditMode ? 'Update' : 'Add'} Bank
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default BankFormModal;

